import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Anchor, Button, Group, Paper, Stack, Table, Text, Title, createStyles} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {
  requireScientificModel,
  type ScientificModelsByExternalReference,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {readableTime} from 'util/DateUtil';

import DetailBreadcrumbs from './lib/DetailBreadcrumbs';
import type {DetailCrumbsReporter} from './lib/detailCrumbs';
import {useModelHubListingTableStyles} from './lib/modelHubListingTableStyles';
import ModelHubLoadingState from './lib/ModelHubLoadingState';
import StatusBadge from './lib/StatusBadge';
import TablePagination from './lib/TablePagination';
import {useBatchDetail} from './lib/useBatchDetail';
import {useVisibilityAwarePolling} from './lib/useVisibilityAwarePolling';
import {batchesPath} from './modelHubRoutes';
import {InputChips} from './prediction-jobs/InputChips';
import type {PredictionJobRow} from './prediction-jobs/PredictionJobTypes';
import {usePredictionJobSearch} from './prediction-jobs/usePredictionJobSearch';

const DEFAULT_PAGE_SIZE = 20;
const POLL_INTERVAL_MS = 30_000;

interface BatchDetailPageProps {
  batchId: string;
  catalogByExternalReference: ScientificModelsByExternalReference;
  onBackToBatches: () => void;
  onSelectPrediction: (predictionId: string, name: string) => void;
  onViewPredictions: (batchId: string) => void;
  // Hands the trail to a host that renders it in its own chrome, suppressing the trail below. Must be
  // stable across renders, as must `onBackToBatches`.
  onCrumbsChange?: DetailCrumbsReporter;
}

export default function BatchDetailPage({
  batchId,
  catalogByExternalReference,
  onBackToBatches,
  onSelectPrediction,
  onViewPredictions,
  onCrumbsChange,
}: BatchDetailPageProps): JSX.Element {
  const {classes} = useStyles();
  const {classes: listingTableClasses} = useModelHubListingTableStyles();
  const {batch, isLoading: batchLoading, error: batchError, refresh: refreshBatch} = useBatchDetail(batchId);

  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // A new batch selection reuses this component instance; reset paging so we start at page one.
  useEffect(() => {
    setOffset(0);
  }, [batchId]);

  // Keyed on the name rather than the batch object, so polling for status does not hand the host a new
  // array every 30s and re-render it for nothing.
  const batchName = batch?.name;
  useEffect(() => {
    if (onCrumbsChange == null || batchName == null) {
      return;
    }
    onCrumbsChange([{label: 'Batches', href: batchesPath(), onClick: onBackToBatches}, {label: batchName}]);
  }, [batchName, onCrumbsChange, onBackToBatches]);

  const predictionsFilters = useMemo(
    () => ({query: '', predictionBatchId: batchId, modelName: null}),
    [batchId]
  );
  // Preserve the batch's prediction creation order.
  const predictionsSort = useMemo(() => ({key: 'createdAt' as const, direction: 'asc' as const}), []);
  const {
    jobs: predictions,
    pagination,
    isLoading: predictionsLoading,
    error: predictionsError,
    refresh: refreshPredictions,
  } = usePredictionJobSearch(predictionsFilters, predictionsSort, offset, pageSize);

  // Poll while the batch (and therefore its predictions) is still progressing.
  const isBatchTerminal = batch != null && (batch.status === 'SUCCEEDED' || batch.status === 'FAILED');
  useVisibilityAwarePolling(batch != null && !isBatchTerminal, POLL_INTERVAL_MS, () => {
    refreshBatch();
    refreshPredictions();
  });

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setOffset(0);
  }, []);

  // The whole page — including breadcrumbs and title — waits for the batch so we can show its
  // persisted name rather than an interim id. Loading and failure states remain headerless.
  if (batchError != null && batch == null) {
    return (
      <Stack
        className={classes.content}
        spacing="lg"
        {...getTestProps({component: 'BatchDetailPage', element: 'root'})}
      >
        <Alert color="red" icon={<BenchlingIcon icon="exclamation-circle" />} title="Failed to load batch">
          <Group spacing="xs">
            <Text size="sm">{batchError}</Text>
            <Anchor component="button" onClick={refreshBatch} size="sm" type="button">
              Retry
            </Anchor>
          </Group>
        </Alert>
      </Stack>
    );
  }

  if (!batchLoading && batch == null) {
    return (
      <Stack
        className={classes.content}
        spacing="lg"
        {...getTestProps({component: 'BatchDetailPage', element: 'root'})}
      >
        <Alert color="red" icon={<BenchlingIcon icon="exclamation-circle" />} title="Batch not found">
          <Group spacing="xs">
            <Text size="sm">This batch might not exist, or you might not have access.</Text>
            <Anchor component="button" onClick={onBackToBatches} size="sm" type="button">
              View batches
            </Anchor>
          </Group>
        </Alert>
      </Stack>
    );
  }

  if (batch == null) {
    return (
      <Stack
        className={classes.content}
        spacing="lg"
        {...getTestProps({component: 'BatchDetailPage', element: 'root'})}
      >
        <ModelHubLoadingState testProps={getTestProps({component: 'BatchDetailPage', element: 'loader'})} />
      </Stack>
    );
  }

  const predictionCount = batch.numPredictionJobs ?? pagination.total;

  const renderFacts = (): JSX.Element => {
    const model = requireScientificModel(catalogByExternalReference, batch.modelName);

    return (
      <Paper
        className={classes.factsCard}
        radius="md"
        withBorder={true}
        {...getTestProps({component: 'BatchDetailPage', element: 'facts'})}
      >
        <Fact label="Status">
          <StatusBadge status={batch.status} />
        </Fact>
        <Fact label="Model">
          <Text fw={600} size="sm">
            {model.displayName}
          </Text>
        </Fact>
        <Fact label="Predictions">
          <Text size="sm">{predictionCount.toLocaleString()}</Text>
        </Fact>
        <Fact label="Created by">
          <Text size="sm">{batch.creatorName || '—'}</Text>
        </Fact>
        <Fact label="Created">
          <Text size="sm">{batch.createdAt ? readableTime(batch.createdAt) : '—'}</Text>
        </Fact>
        <Fact label="Last modified">
          <Text size="sm">{batch.modifiedAt ? readableTime(batch.modifiedAt) : '—'}</Text>
        </Fact>
        <Fact label="Batch ID">
          <Text className={classes.monoValue} size="sm">
            {batch.id}
          </Text>
        </Fact>
      </Paper>
    );
  };

  const renderPredictions = (): JSX.Element => {
    if (predictionsError != null) {
      return (
        <Alert
          color="red"
          icon={<BenchlingIcon icon="exclamation-circle" />}
          title="Failed to load predictions"
        >
          <Group spacing="xs">
            <Text size="sm">{predictionsError}</Text>
            <Anchor component="button" onClick={refreshPredictions} size="sm" type="button">
              Retry
            </Anchor>
          </Group>
        </Alert>
      );
    }

    if (predictionsLoading && predictions.length === 0) {
      return (
        <ModelHubLoadingState
          testProps={getTestProps({component: 'BatchDetailPage', element: 'predictions-loader'})}
        />
      );
    }

    if (predictions.length === 0) {
      return (
        <div className={classes.emptyState}>
          <Text color="dimmed" size="sm">
            This batch has no predictions.
          </Text>
        </div>
      );
    }

    return (
      <Stack className={listingTableClasses.listingContent} spacing="lg">
        <Paper
          className={listingTableClasses.tablePaper}
          radius="md"
          withBorder={true}
          {...getTestProps({component: 'BatchDetailPage', element: 'predictions-table'})}
        >
          {/*
            Columns are Name · Inputs · Status. A Details column (per-job ipTM/pLDDT and per-job
            failure messages) is deferred until the backend exposes per-prediction data.
          */}
          <Table className={classes.table} horizontalSpacing={14} verticalSpacing={10}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Inputs</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {predictions.map((prediction) => (
                <PredictionRow
                  key={prediction.id}
                  name={prediction.name}
                  onSelect={onSelectPrediction}
                  prediction={prediction}
                />
              ))}
            </tbody>
          </Table>
        </Paper>
        <TablePagination
          onOffsetChange={setOffset}
          onPageSizeChange={handlePageSizeChange}
          pagination={pagination}
        />
      </Stack>
    );
  };

  return (
    <Stack
      className={classes.content}
      spacing="lg"
      {...getTestProps({component: 'BatchDetailPage', element: 'root'})}
    >
      <Stack spacing="md">
        {onCrumbsChange == null && (
          <DetailBreadcrumbs
            crumbs={[{label: 'Batches', href: batchesPath(), onClick: onBackToBatches}, {label: batch.name}]}
          />
        )}

        <Group noWrap={true} position="apart">
          <Group spacing={12}>
            <Title className={classes.title} order={1}>
              {batch.name}
            </Title>
            <StatusBadge status={batch.status} />
          </Group>
          <Button
            leftIcon={<BenchlingIcon icon="table-list" />}
            onClick={() => {
              trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.BATCHES_VIEW_PREDICTIONS_CLICKED);
              onViewPredictions(batchId);
            }}
            variant="secondary"
          >
            View predictions
          </Button>
        </Group>
      </Stack>

      {renderFacts()}

      <Stack className={classes.predictionsSection} spacing="xs">
        <Text className={classes.sectionLabel}>
          Predictions{' '}
          <Text component="span" color="dimmed">
            · {predictionCount.toLocaleString()} total
          </Text>
        </Text>
        {renderPredictions()}
      </Stack>
    </Stack>
  );
}

function Fact({label, children}: {children: React.ReactNode; label: string}): JSX.Element {
  const {classes} = useStyles();
  return (
    <div className={classes.fact}>
      <div className={classes.factLabel}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

function PredictionRow({
  name,
  onSelect,
  prediction,
}: {
  name: string;
  onSelect: (predictionId: string, name: string) => void;
  prediction: PredictionJobRow;
}): JSX.Element {
  const {classes} = useStyles();
  return (
    <tr
      {...getTestProps({component: 'BatchDetailPage', element: 'predictionRow'})}
      className={classes.clickableRow}
      onClick={() => {
        trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.BATCHES_PREDICTION_OPENED_FROM_BATCH);
        onSelect(prediction.id, name);
      }}
    >
      <td>
        <Text
          {...getTestProps({component: 'BatchDetailPage', element: 'predictionName'})}
          className={classes.nameCell}
        >
          {name}
        </Text>
      </td>
      <td>
        <InputChips configJson={prediction.configJson} />
      </td>
      <td>
        {/* Per-prediction status does not exist yet, so every row shows the batch-level status. */}
        <StatusBadge status={prediction.status} />
      </td>
    </tr>
  );
}

const useStyles = createStyles((theme) => ({
  clickableRow: {
    cursor: 'pointer',
  },
  content: {
    height: '100%',
    minHeight: 0,
  },
  emptyState: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 10,
    padding: '40px 0',
    textAlign: 'center' as const,
  },
  fact: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 6,
    minWidth: 0,
  },
  factLabel: {
    color: theme.colors.gray[6],
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.03em',
    textTransform: 'uppercase' as const,
  },
  factsCard: {
    display: 'grid',
    gap: '18px 28px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    padding: '20px 22px',
  },
  monoValue: {
    fontFamily: theme.fontFamilyMonospace,
  },
  nameCell: {
    fontFamily: theme.fontFamilyMonospace,
    fontWeight: 600,
  },
  predictionsSection: {
    flex: '1 1 0',
    minHeight: 0,
  },
  sectionLabel: {
    fontSize: 11.5,
    fontWeight: 600,
    letterSpacing: '0.03em',
    textTransform: 'uppercase' as const,
  },
  table: {
    '& thead th': {
      backgroundColor: theme.colors.gray[0],
      borderBottom: `1px solid ${theme.colors.gray[3]}`,
      color: theme.colors.gray[6],
      fontSize: 11.5,
      fontWeight: 600,
      letterSpacing: '0.03em',
      position: 'sticky',
      textAlign: 'left' as const,
      textTransform: 'uppercase' as const,
      top: 0,
      zIndex: 1,
    },

    '& tbody tr:hover': {
      backgroundColor: theme.colors.gray[0],
    },

    '& td, & th': {
      verticalAlign: 'middle',
    },
  },
  title: {
    fontFamily: theme.fontFamilyMonospace,
  },
}));
